import { createSelector } from 'reselect';
import { AppState } from '../../types';
import { getNamedElementsById } from '../namedElements/selectors';
import { WatchedElement } from './types';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { getIsReady } from '@fullstory/redux-util';
import { createDeepEqualSelector } from '@fullstory/redux-util/utils/selectors';

export const rootSelector = (state: AppState) => state.watchedElements;
export const getAllWatchedElementsLoading = (state: AppState) =>
  // watched elements are fetched / stored in the web recording settings request
  state.settings.webRecording.settingsRequest.loading;

export const getAllWatchedElementsReady = (state: AppState) =>
  getIsReady(state.settings.webRecording.settingsRequest);

const _watchesSelector = createSelector(rootSelector, ({ watches }) => watches || []);
export const watchesSelector = createDeepEqualSelector(_watchesSelector, watches => watches);

export const getWatchesInitialized = createSelector(
  rootSelector,
  ({ watchesInitialized }) => watchesInitialized,
);

export const getWatchedNamedElementIds = createSelector(
  watchesSelector,
  watches => new Set(watches.map(watch => watch.namedElementId).filter(id => id)),
);

export const hydratedWatchesSelector = createSelector(
  watchesSelector,
  getNamedElementsById,
  (watches, namedElementsById) =>
    watches.map((watch: WatchedElement): WatchedElement => {
      if (watch.namedElementId) {
        // need to hydrate respective display values
        const namedElement = namedElementsById.get(watch.namedElementId);
        const watchUpdated = Timestamp.toMillis(watch.lastUpdated);
        const namedElementUpdated = Timestamp.toMillis(namedElement?.metadata?.lastUpdated);
        return {
          ...watch,
          label: namedElement?.namedElement?.name ?? '',
          notes: namedElement?.namedElement?.description ?? '',
          selectors: namedElement?.namedElement?.platformSelectors.map(ps => ps.selector) ?? [''],
          // and take the most recent updated value from either the watch rule or the named element for more useful context
          lastUpdated:
            watchUpdated > namedElementUpdated
              ? watch.lastUpdated
              : namedElement?.metadata?.lastUpdated ?? null,
        };
      } else {
        // no modifications needed
        return watch;
      }
    }),
);
